import React, { MouseEvent } from "react";
import { ContextMenuItem } from "../../types/system/ContextMenuItem";

import "./ContextMainMenu.scss";
import { createPortal } from "react-dom";
import { ContextMenuContext } from "./ContextMenuContext";
import Utilities from "../../foundation/Utility";

interface ContextMenuProps {
    menuItemClicked?: () => void;
}

interface ContextMenuState {
    menuRef: React.RefObject<HTMLDivElement>;
}

class ContextMenu extends React.Component<ContextMenuProps, ContextMenuState> {
    static contextType = ContextMenuContext;
    context!: React.ContextType<typeof ContextMenuContext>;

    state = {
        menuRef: React.createRef<HTMLDivElement>(),
    };

    componentDidMount(): void {
        window.addEventListener("click", this.contextDismissedHandler);
        window.addEventListener("contextmenu", this.contextRequestedHandler);
    }
    componentWillUnmount(): void {
        window.removeEventListener("click", this.contextDismissedHandler);
        window.removeEventListener("contextmenu", this.contextRequestedHandler);
    }

    contextRequested(event: MouseEvent) {
        const element = this.state.menuRef.current;

        if (element) {
            setTimeout(() => {
                let adjustmentX =
                    window.innerWidth - (event.clientX + element.offsetWidth);
                let adjustmentY =
                    window.innerHeight - (event.clientY + element.offsetHeight);

                element.style.top = `${
                    event.clientY + Math.min(adjustmentY, 0)
                }px`;
                element.style.left = `${
                    event.clientX + Math.min(adjustmentX, 0)
                }px`;
            });

            event.preventDefault();
        }

        let contextTarget = event.target as HTMLElement;

        while (contextTarget && !contextTarget.getAttribute("data-contextkey"))
            contextTarget = contextTarget.parentElement;

        contextTarget = contextTarget ?? (event.target as HTMLElement);

        this.context.setContext(
            contextTarget,
            Utilities.getSelectionRange(contextTarget, document.getSelection()),
            contextTarget?.getAttribute("data-contextvalue") ?? null,
            contextTarget?.getAttribute("data-contextkey") ?? "",
        );
    }
    contextRequestedHandler = this.contextRequested.bind(this);
    async contextDismissed() {
        await Utilities.sleep(100);

        const element = this.state.menuRef.current;

        if (element)
            setTimeout(() => {
                element.style.top = "-1000px";
            });
    }
    contextDismissedHandler = this.contextDismissed.bind(this);

    itemClicked(item: ContextMenuItem, event: MouseEvent) {
        item.handler?.(event, this.context);
    }

    render() {
        return createPortal(
            <div
                ref={this.state.menuRef}
                className="context-menu">
                <ul className="menu">
                    {(this.context.menuItems ?? [])
                        .sort(Utilities.getSorter(["order"]))
                        .concat(
                            ...this.context.additionalMenuItems.sort(
                                Utilities.getSorter(["order"]),
                            ),
                        )
                        .filter(
                            item =>
                                (!item.contextKey ||
                                    item.contextKey
                                        .split(";")
                                        .includes(this.context.getKey())) &&
                                !item.hideHandler?.(this.context),
                        )
                        .map((item, index) =>
                            item.separator ? (
                                <li key={index}>
                                    <hr></hr>
                                </li>
                            ) : (
                                <li key={index}>
                                    <button
                                        disabled={
                                            item.disabled ||
                                            item.disabledHandler?.(this.context)
                                        }
                                        onClick={event =>
                                            this.itemClicked(item, event)
                                        }>
                                        <i className={`${item.iconStyle}`}>
                                            {item.icon}
                                        </i>
                                        <span>{item.name}</span>
                                        <span>{item.shortcut}</span>
                                    </button>
                                </li>
                            ),
                        )}
                </ul>
            </div>,
            document.querySelector(".page-elements"),
        );
    }
}

export default ContextMenu;
